import React, { ReactNode, useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePortal, useViewportWidth } from '@/hooks/shared';
import { useAppFilters } from '@/hooks/shared/useAppFilters';
import { cn } from '@/lib/utils';
import { BaseTabsTrigger } from './BaseTabsTrigger';
import { FILTER_PARAMS, PORTAL_TARGETS } from '@/types/shared/navigation-tabs.enums';

export type TabType = {
  id: string;
  title: string | ReactNode;
  element?: any;
  icon?: ReactNode;
  disabled?: boolean;
  tooltip?: string;
};
export type TabVariant = 'primary' | 'secondary' | 'secondary-plain' | 'secondary-line';

interface BaseTabsProps {
  tabs: TabType[];
  defaultValue: string;
  updateUrl?: boolean;
  hideTabTriggers?: boolean;
  confirmBeforeChange?: boolean;
  onClick?: (tabId: string) => void;
  paramAlias?:
    | FILTER_PARAMS.TAB
    | FILTER_PARAMS.ASSETS_TYPE
    | FILTER_PARAMS.ASSETS_PREVIEW
    | FILTER_PARAMS.ASSET_STUDIO
    | FILTER_PARAMS.SENSOR_DATA
    | FILTER_PARAMS.ASSETS_SECTION;
  variant?: TabVariant;
  renderFixedElement?: () => ReactNode;
  renderRightPane?: (slot?: React.ReactNode) => React.ReactNode;
  renderCustomPane?: () => ReactNode;
  renderTabListInto?: PORTAL_TARGETS;
  createContainerById?: string;
  classNames?: {
    tabs?: string;
    tabList?: {
      container?: string;
      content?: string;
    };
    tabContent?: string;
  };
}

export const BaseTabs = ({
  tabs,
  defaultValue,
  updateUrl = true,
  onClick,
  variant = 'primary',
  paramAlias = FILTER_PARAMS.TAB,
  hideTabTriggers = true,
  classNames,
  renderFixedElement,
  renderRightPane,
  renderCustomPane,
  confirmBeforeChange = false,
  renderTabListInto,
  createContainerById,
}: BaseTabsProps) => {
  const { isTablet } = useViewportWidth();
  const filters = useAppFilters();
  // Local state for tab value when updateUrl is false (no URL param syncing)
  const [localTab, setLocalTab] = useState(defaultValue);
  const portal = usePortal(renderTabListInto);

  // Sync tab value when defaultValue changes
  useEffect(() => {
    if (updateUrl) {
      filters.setFilters({ [paramAlias]: defaultValue });
    } else {
      setLocalTab(defaultValue);
    }
  }, [defaultValue, updateUrl]);

  // Determine active tab value: from URL params or local state
  const activeTabValue = updateUrl
    ? (filters?.[paramAlias as 'tab'] || defaultValue)
    : localTab;

  // Update the tab when clicked
  const handleTabClick = (tabId: string) => {
    if (updateUrl) {
      filters.setFilters({ [paramAlias]: tabId });
    } else {
      setLocalTab(tabId);
    }
    onClick && onClick(tabId);
  };

  // Handle tab click when confirmation is needed before changing tabs
  const handleTabClickWithConfirmation = (tabId: string) => {
    onClick?.(tabId);
  };

  const baseClassNames = {
    primary: {
      tabs: '',
      tabList: {
        container: 'flex min-w-full justify-between items-center',
        content: `z-20 ${
          isTablet ? 'fixed top-header w-screen left-0 rounded-none border-2 py-1 px-1 h-100' : 'h-100 my-3'
        }`,
      },
      tabContent: `overflow-auto max-h-main-mobile realtive ${hideTabTriggers ? '' : 'pb-6'}`,
    },
    tabsContent: '',
    secondary: {
      tabs: '',
      tabList: {
        container: 'flex min-w-full justify-between items-center bg-gray-200 dark:bg-dark-bg-elevated px-4 rounded-lg ',
        content: cn('y-0 m-0 p-2 w-full justify-start gap-2 bg-tranparent'),
      },
      tabContent: `overflow-auto flex flex-col flex-1 ${hideTabTriggers ? '' : 'pb-6'}`,
    },
    'secondary-plain': {
      tabs: '',
      tabList: {
        container: 'flex min-w-full justify-between items-center bg-gray-200 dark:bg-dark-bg-elevated',
        content: cn('y-0 m-0 p-2 w-full justify-start gap-2 bg-tranparent'),
      },
      tabContent: `overflow-auto flex flex-col flex-1 ${hideTabTriggers ? '' : ''}`,
    },
    'secondary-line': {
      tabs: '',
      tabList: {
        container:
          'flex min-w-full justify-between items-center bg-gray-200 dark:bg-dark-bg-elevated px-2 rounded-lg rounded-none',
        content: cn('y-0 m-0 p-2 w-full justify-start bg-tranparent'),
      },
      tabContent: `overflow-auto flex flex-col flex-1  ${hideTabTriggers ? '' : 'pb-6'}`,
    },
  };

  const TabListBlock = () => (
    <TabsList className={cn(baseClassNames[variant as 'primary'].tabList.content, classNames?.tabList?.content || '')}>
      {tabs.map((tab: TabType) => (
        <BaseTabsTrigger
          variant={variant}
          isTablet={isTablet}
          hideTabTriggers={hideTabTriggers}
          key={tab.id}
          onTabClick={confirmBeforeChange ? handleTabClickWithConfirmation : handleTabClick}
          {...tab}
        />
      ))}
    </TabsList>
  );

  const tabListContainer = () => (
    <div className={cn(baseClassNames[variant as 'primary'].tabList.container, classNames?.tabList?.container || '')}>
      <TabListBlock />
      <div id="tab-line-right-pane" className="w-full">
        {renderRightPane ? renderRightPane() : ''}

        {createContainerById && (
          <div
            id={createContainerById}
            className={cn(
              'flex items-center justify-between pl-6 gap-6',
              'xl:ml-6 xl:border-l xl:border-[#B1B9C3] w-fit'
            )}
          ></div>
        )}
      </div>
    </div>
  );

  return (
    <Tabs
      defaultValue={activeTabValue}
      value={activeTabValue}
      className={cn('w-full h-full bg-transparent', classNames?.tabs || '')}
    >
      {renderTabListInto ? portal?.render(tabListContainer()) : tabListContainer()}

      {renderCustomPane ? renderCustomPane() : ''}

      {renderFixedElement
        ? renderFixedElement()
        : tabs.map((tab: TabType) => (
            <TabsContent
              key={tab.id + '_tabs_content'}
              value={tab.id}
              className={cn(baseClassNames[variant as 'primary'].tabContent, classNames?.tabContent || '')}
            >
              {tab.element}
            </TabsContent>
          ))}
    </Tabs>
  );
};
